import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

// Define the shape of the context state
interface IntegrationsState {
  isMenuPinned: boolean;
}

// Define a generic action that automatically infers from IntegrationsState
interface Action<K extends keyof IntegrationsState> {
  type: K;
  value: IntegrationsState[K];
}

// Helper function to get initial state from window.store if available
// This prevents race condition by reading the actual state on mount
function getInitialState(): IntegrationsState {
  // Try to read from window.store if available (for soft refresh support)
  if (typeof window !== "undefined" && window.store) {
    try {
      const state = window.store.getState();
      return {
        isMenuPinned: state.user?.isMenuPinned ?? false,
      };
    } catch (error) {
      // Fallback to default if store read fails
      console.warn("Error reading initial state from window.store:", error);
    }
  }
  return {
    isMenuPinned: false,
  };
}

// Initial state - read from store if available to prevent race condition
export const initialState: IntegrationsState = getInitialState();

// Reducer function that infers the key and value types
const reducer = <K extends keyof IntegrationsState>(
  state: IntegrationsState,
  action: Action<K>,
): IntegrationsState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const IntegrationsContext = createContext<
  | {
      integrationsState: IntegrationsState;
      updateIntegrationsState: <K extends keyof IntegrationsState>(
        key: K,
        value: IntegrationsState[K],
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function IntegrationsProvider({ children }: { children: ReactNode }) {
  const [integrationsState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateIntegrationsState = useCallback(
    <K extends keyof IntegrationsState>(
      key: K,
      value: IntegrationsState[K],
    ) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    [],
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      integrationsState,
      updateIntegrationsState,
    }),
    [integrationsState, updateIntegrationsState],
  );

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
}

// Custom hook to use the Integrations context
export const useIntegrationsContext = () => {
  const context = useContext(IntegrationsContext);

  if (!context) {
    return {
      integrationsState: initialState,
      updateIntegrationsState: () => {},
    };
  }

  return context;
};

// Hook to access the Integrations state (for backward compatibility)
export function useIntegrationsState() {
  const { integrationsState } = useIntegrationsContext();
  return {
    isMenuPinned: integrationsState.isMenuPinned,
  };
}
